import { useMemo, useState } from 'react';
import { Alert, Box, Chip, CircularProgress, LinearProgress, Link, Paper, Stack, Step, StepLabel, Stepper, Typography } from '@mui/material';
import { useAgentRunEvents, usePlanApprovals } from '@diggens/shared-hooks';
import { agentStyles } from '../../theme/theme';
import { PendingCard } from './PendingCard';
import { RunOutcomeChip } from './RunList';
import { agentLabel, PLAN_TOOLS, PlanSteps, ResultText, describeToolInput, formatClock, formatWhen, phaseStates, reduceEvents, useNow, type AgentNode, type FeedItem } from '@diggens/chat';

const TONE_COLORS: Record<NonNullable<FeedItem['tone']>, string> = {
  tool: 'primary.main',
  ok: 'success.main',
  bad: 'error.main',
  wait: 'warning.main',
};

function AgentCard({ node, now, isRoot }: { node: AgentNode; now: number; isRoot: boolean }) {
  const color = {
    running: agentStyles.statusColors.processing,
    waiting: agentStyles.statusColors.warning,
    succeeded: agentStyles.statusColors.success,
    failed: agentStyles.statusColors.error,
  }[node.status];
  // A plan tool's input is plan plumbing; the plan itself shows below
  const input = node.currentTool && !PLAN_TOOLS.has(node.currentTool.name) ? describeToolInput(node.currentTool.input) : '';
  return (
    <Paper variant="outlined" sx={{ display: 'grid', gridTemplateColumns: '6px 1fr', overflow: 'hidden' }}>
      <Box sx={{ backgroundColor: color, ...(node.status === 'running' ? agentStyles.agentPulse : {}) }} />
      <Box sx={{ px: 1.5, py: 1, minWidth: 0 }}>
        <Stack direction="row" spacing={1} sx={{ alignItems: 'baseline', flexWrap: 'wrap' }}>
          <Typography variant={isRoot ? 'subtitle1' : 'subtitle2'} sx={{ fontWeight: 600 }}>
            {agentLabel(node.agent)}
          </Typography>
          {/* The root's title is the request, already the run's heading */}
          <Typography variant="caption" color="text.secondary" sx={{ flex: 1, minWidth: 0, overflowWrap: 'anywhere' }}>
            {isRoot ? '' : node.title}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ fontVariantNumeric: 'tabular-nums' }}>
            {formatClock((node.finishedAt ? Date.parse(node.finishedAt) : now) - Date.parse(node.startedAt))}
          </Typography>
        </Stack>
        <Typography variant="body2" sx={{ mt: 0.5, overflowWrap: 'anywhere' }}>
          {node.currentTool ? (
            <>
              <Box component="span" sx={{ fontWeight: 600, mr: 1 }}>
                {node.currentTool.name}
              </Box>
              <Box component="span" sx={{ fontSize: '0.8rem', color: 'text.secondary' }}>
                {input}
              </Box>
            </>
          ) : (
            (node.lastActivity ?? (node.status === 'running' ? 'Thinking' : ''))
          )}
        </Typography>
      </Box>
    </Paper>
  );
}

/** One run: what was asked, how each phase went, the answer, the plan and its results, the agents, and their activity. */
export function RunMonitor({ runId }: { runId: string }) {
  const { data, isLoading, error } = useAgentRunEvents(runId);
  const { data: approvals } = usePlanApprovals();
  const view = useMemo(() => (data ? reduceEvents(data.events) : null), [data]);
  const [filter, setFilter] = useState<string>('all');
  const [openOutputs, setOpenOutputs] = useState<ReadonlySet<string>>(new Set());
  const now = useNow();

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }
  if (error || !data || !view) return <Alert severity="error">{error instanceof Error ? error.message : 'Could not load this run'}</Alert>;

  const { run } = data;
  const [root, ...children] = view.agents;
  const steps = view.plan?.steps ?? [];
  const stepsDone = steps.filter((step) => step.status === 'succeeded').length;
  const activeChildren = children.filter((child) => child.status === 'running').length;
  const toolCalls = view.agents.reduce((total, node) => total + node.toolCalls, 0);
  const elapsed = (run.finishedAt ? Date.parse(run.finishedAt) : now) - Date.parse(run.startedAt);
  const feed = view.feed.filter((item) => filter === 'all' || item.runId === filter).reverse();
  const phases = run.agent === 'manager' ? phaseStates(view) : null;
  const pending = view.plan ? approvals?.pending.find((approval) => approval.planId === view.plan!.planId) : undefined;
  // Who decided the plan; the approvals hold the latest decisions, so an older one shows only its outcome
  const decided = view.plan ? approvals?.decided.find((approval) => approval.planId === view.plan!.planId) : undefined;
  const toggleOutput = (id: string) =>
    setOpenOutputs((open) => {
      const next = new Set(open);
      if (!next.delete(id)) next.add(id);
      return next;
    });

  return (
    <Stack spacing={2}>
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Stack direction="row" spacing={1} sx={{ alignItems: 'flex-start', justifyContent: 'space-between', mb: 1 }}>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="h6" sx={{ overflowWrap: 'anywhere' }}>
              {run.title}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {agentLabel(run.agent)} · {run.userName ?? 'System'} · {run.trigger} · started {formatWhen(run.startedAt)}
            </Typography>
          </Box>
          <RunOutcomeChip run={run} />
        </Stack>

        {phases && (
          <Stepper activeStep={-1} sx={{ my: 2 }}>
            {phases.map((phase) => (
              <Step key={phase.id} active={phase.state === 'active'} completed={phase.state === 'complete'} disabled={phase.state === 'skipped'}>
                <StepLabel
                  error={phase.state === 'failed'}
                  optional={
                    phase.caption ? (
                      <Typography variant="caption" color={phase.state === 'failed' ? 'error' : 'text.secondary'}>
                        {phase.caption}
                      </Typography>
                    ) : undefined
                  }
                >
                  {phase.label}
                </StepLabel>
              </Step>
            ))}
          </Stepper>
        )}

        {steps.length > 0 && <LinearProgress variant="determinate" value={(stepsDone / steps.length) * 100} sx={{ height: 8, borderRadius: 4, mb: 2 }} />}

        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(4, 1fr)' }, gap: 2 }}>
          {[
            ['Steps done', steps.length ? `${stepsDone} / ${steps.length}` : '–'],
            ['Active subagents', String(activeChildren)],
            ['Elapsed', formatClock(elapsed)],
            ['Tool calls', String(toolCalls)],
          ].map(([label, value]) => (
            <Box key={label}>
              <Typography variant="caption" color="text.secondary">
                {label}
              </Typography>
              <Typography variant="h6" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                {value}
              </Typography>
            </Box>
          ))}
        </Box>
      </Paper>

      {run.stalled && <Alert severity="warning">This run has sent nothing for over 2 minutes. Its server may have stopped; it will not resume.</Alert>}
      {root?.status === 'failed' && <Alert severity="error">{root.lastActivity}</Alert>}

      {view.answer && (
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
            Answer
          </Typography>
          <ResultText text={view.answer} />
        </Paper>
      )}

      {pending ? (
        <PendingCard approval={pending} now={now} />
      ) : (
        view.plan && (
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Stack direction="row" spacing={1} sx={{ alignItems: 'center', mb: 0.5 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                Plan
              </Typography>
              {view.plan.revision > 1 && <Chip label="Revised" size="small" variant="outlined" />}
              {view.plan.decision && (
                <Chip
                  label={{ approved: 'Approved', rejected: 'Rejected', expired: 'Expired' }[view.plan.decision]}
                  size="small"
                  color={view.plan.decision === 'approved' ? 'success' : 'error'}
                  variant="outlined"
                />
              )}
            </Stack>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5, overflowWrap: 'anywhere' }}>
              {view.plan.goal}
            </Typography>
            <PlanSteps steps={view.plan.steps} />
            {decided?.decidedBy && decided.decidedAt && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                {decided.decision === 'approved' ? 'Approved' : 'Rejected'} by {decided.decidedBy} at {formatWhen(decided.decidedAt)}
              </Typography>
            )}
            {view.plan.note && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                Approver’s note: {view.plan.note}
              </Typography>
            )}
          </Paper>
        )
      )}

      <Paper variant="outlined" sx={{ p: 2 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1.5 }}>
          Agents
        </Typography>
        {root && <AgentCard node={root} now={now} isRoot />}
        {children.length > 0 && (
          <Stack spacing={1} sx={{ mt: 1, ml: 2, pl: 2, borderLeft: 2, borderColor: 'divider' }}>
            {children.map((child) => (
              <AgentCard key={child.runId} node={child} now={now} isRoot={false} />
            ))}
          </Stack>
        )}
      </Paper>

      <Paper variant="outlined" sx={{ p: 2 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
          Activity
        </Typography>
        <Stack direction="row" spacing={0.5} sx={{ flexWrap: 'wrap', gap: 0.5, mb: 1 }}>
          {[{ id: 'all', label: 'All' }, ...view.agents.map((node) => ({ id: node.runId, label: agentLabel(node.agent) }))].map((chip) => (
            <Chip
              key={chip.id}
              label={chip.label}
              size="small"
              onClick={() => setFilter(chip.id)}
              color={filter === chip.id ? 'primary' : 'default'}
              variant={filter === chip.id ? 'filled' : 'outlined'}
            />
          ))}
        </Stack>
        <Box component="ol" sx={{ listStyle: 'none', m: 0, p: 0, maxHeight: 420, overflowY: 'auto' }}>
          {feed.map((item) => (
            <Box component="li" key={item.id} sx={{ display: 'grid', gridTemplateColumns: '3.5em 1fr', gap: 1, py: 0.75, borderTop: 1, borderColor: 'divider' }}>
              <Typography variant="caption" color="text.secondary" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                {formatClock(Date.parse(item.at) - Date.parse(run.startedAt))}
              </Typography>
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="body2" sx={{ overflowWrap: 'anywhere' }}>
                  <Box component="span" sx={{ fontWeight: 600, mr: 1 }}>
                    {agentLabel(item.agent)}
                  </Box>
                  <Box component="span" sx={{ color: item.tone ? TONE_COLORS[item.tone] : 'text.primary' }}>
                    {item.text}
                  </Box>
                  {item.output !== null && (
                    <Link component="button" variant="caption" onClick={() => toggleOutput(item.id)} sx={{ ml: 1, verticalAlign: 'baseline' }}>
                      {openOutputs.has(item.id) ? 'Hide output' : 'Output'}
                    </Link>
                  )}
                </Typography>
                {item.output !== null && openOutputs.has(item.id) && (
                  <Box component="pre" sx={{ m: 0, mt: 0.5, p: 1, bgcolor: 'action.hover', borderRadius: 1, fontSize: '0.75rem', whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>
                    {item.output}
                  </Box>
                )}
              </Box>
            </Box>
          ))}
        </Box>
      </Paper>
    </Stack>
  );
}
